import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Schedule,
  Assignment,
  MonetizationOn,
  SwapHoriz,
  Undo,
  CancelPresentation,
  Info,
  AttachMoney,
} from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';

interface RefundRequest {
  id: string;
  order_item_id: string;
  type: 'cancel' | 'exchange' | 'refund';
  reason: string;
  detail: string;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed';
  admin_note?: string;
  processed_at?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  image_url?: string | string[];
  // 조인된 데이터
  order_items?: {
    id: string;
    product_name: string;
    total_price: number;
    orders: {
      id: string;
      profiles: {
        nick_name: string;
        email: string;
      };
    };
  };
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function RefundManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RefundRequest | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [adminNote, setAdminNote] = useState('');
  const [requests, setRequests] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentErrorDialogOpen, setPaymentErrorDialogOpen] = useState(false);

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);

  // 데이터 로드
  useEffect(() => {
    loadRefundRequests();
  }, [user]);

  const loadRefundRequests = async () => {
    if (!user) {
      console.error('User not authenticated');
      setRequests([]);
      return;
    }

    try {
      setLoading(true);

      // Admin 권한 확인
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profileError || profile?.role !== 'admin') {
        console.error('User is not admin or profile error:', profileError);
        setRequests([]);
        return;
      }

      // RLS 정책이 설정되었으므로 실제 데이터 로드
      console.log('Admin access confirmed, loading refund requests...');

      const { data, error } = await supabase
        .from('cancel_exchange_requests')
        .select(`
          *,
          order_items (
            id,
            product_name,
            total_price,
            orders (
              id,
              profiles (
                nick_name,
                email
              )
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading refund requests:', error);
        setRequests([]);
        return;
      }

      console.log('Refund requests loaded:', data);
      setRequests(data || []);
    } catch (error) {
      console.error('Error loading refund requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'cancel': return 'error';
      case 'exchange': return 'info';
      case 'return': return 'warning';
      default: return 'default';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'cancel': return t('refund.cancelTypeLabel');
      case 'exchange': return t('refund.exchangeTypeLabel');
      case 'return': return t('refund.refundTypeLabel');
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cancel': return <CancelPresentation />;
      case 'exchange': return <SwapHoriz />;
      case 'return': return <Undo />;
      default: return <Assignment />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'processing': return 'info';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return t('refund.reviewingStatus');
      case 'approved': return t('refund.approvedStatus');
      case 'rejected': return t('refund.rejectedStatus');
      case 'processing': return t('refund.processingCard');
      case 'completed': return t('refund.completedTab');
      default: return status;
    }
  };

  const handleViewDetail = (request: RefundRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleAction = (request: RefundRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest) return;
    if (!user) {
      alert(t('refund.loginRequired'));
      return;
    }

    try {

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role !== 'admin') {
        alert(t('refund.adminRequired'));
        return;
      }

      const newStatus = actionType === 'approve' ? 'approved' : 'rejected';

      if (actionType === 'approve' && selectedRequest.type !== 'exchange') {
        try {
          const paymentCancelResponse = await fetch('https://mxpdcnqujyxsodymliuj.supabase.co/functions/v1/cancel-payment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
            },
            body: JSON.stringify({
              order_id: selectedRequest.order_items?.orders?.id,
              order_item_id: selectedRequest.order_item_id,
              refund_amount: selectedRequest.order_items?.total_price,
              cancel_reason: `${selectedRequest.type === 'cancel' ? t('refund.orderCancel') : t('refund.returnLabel')}: ${selectedRequest.reason}`,
              admin_note: adminNote
            })
          });

          const cancelResult = await paymentCancelResponse.json();

          if (!paymentCancelResponse.ok) {
            console.error('❌ payment cancel failed:', cancelResult);
            alert(t('refund.paymentCancelError', { error: cancelResult.error }));
            return;
          }
        } catch (paymentError) {
          console.error('💥 payment cancel error:', paymentError);
          alert(t('refund.paymentCancelNetworkError'));
          return;
        }
      }

      const { error } = await supabase
        .from('cancel_exchange_requests')
        .update({
          status: newStatus,
          admin_note: adminNote,
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (error) {
        console.error('Error updating request:', error);
        alert(t('refund.processError'));
        return;
      }

      await loadRefundRequests();
      setActionDialogOpen(false);
      setAdminNote('');
      const actionLabel = actionType === 'approve' ? t('refund.approved') : t('refund.rejected');
      const paymentNote = actionType === 'approve' && selectedRequest.type !== 'exchange' ? t('refund.paymentCancelComplete') : '';
      alert(t('refund.actionSuccess', { action: actionLabel, paymentNote }));
    } catch (error) {
      console.error('Error updating request:', error);
      alert(t('refund.processError'));
    }
  };

  const filteredRequests = requests.filter(request => {
    const customerName = request.order_items?.orders?.profiles?.nick_name || '';
    const productName = request.order_items?.product_name || '';
    const orderId = request.order_items?.orders?.id || '';

    const matchesSearch =
      orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesType = typeFilter === 'all' || request.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const getFilteredByTab = () => {
    switch (tabValue) {
      case 0: return filteredRequests; // 전체
      case 1: return filteredRequests.filter(r => r.status === 'pending'); // 대기중
      case 2: return filteredRequests.filter(r => ['approved', 'processing'].includes(r.status)); // 처리중
      case 3: return filteredRequests.filter(r => ['completed', 'rejected'].includes(r.status)); // 완료
      default: return filteredRequests;
    }
  };

  const displayRequests = getFilteredByTab();

  // 페이지네이션 계산
  const totalPages = Math.ceil(displayRequests.length / rowsPerPage);
  const paginatedRequests = displayRequests.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // 필터 변경 시 첫 페이지로 이동
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, typeFilter, tabValue]);

  // 통계 계산
  const totalRequests = requests.length;
  const pendingRequests = requests.filter(r => r.status === 'pending').length;
  const processingRequests = requests.filter(r => ['approved', 'processing'].includes(r.status)).length;
  const totalRefundAmount = requests
    .filter(r => r.type !== 'exchange' && r.status !== 'rejected')
    .reduce((sum, r) => sum + (r.order_items?.total_price || 0), 0);

  return (
    <Box sx={{
      p: 3,
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: '#1f2937'
        }}
      >
        {t('refund.management')}
      </Typography>

      {/* 요약 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#dbeafe'
                }}>
                  <Assignment sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('refund.totalRequestsCard')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 700
                    }}
                  >
                    {totalRequests}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fef3c7'
                }}>
                  <Schedule sx={{ color: '#f59e0b' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('refund.pendingReviewCard')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#f59e0b',
                      fontWeight: 700
                    }}
                  >
                    {pendingRequests}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#dbeafe'
                }}>
                  <Info sx={{ color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('refund.processingCard')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 700
                    }}
                  >
                    {processingRequests}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fee2e2'
                }}>
                  <AttachMoney sx={{ color: '#ef4444' }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {t('refund.refundAmountCard')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#ef4444',
                      fontWeight: 700
                    }}
                  >
                    ₩{totalRefundAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{
        p: 3,
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        {/* 탭 */}
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('refund.allTab')} />
          <Tab label={t('refund.pendingTab')} />
          <Tab label={t('refund.processingTab')} />
          <Tab label={t('refund.completedTab')} />
        </Tabs>

        {/* 필터 및 검색 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={t('refund.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('refund.processingStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('refund.processingStatus')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="pending">{t('refund.reviewingStatus')}</MenuItem>
                <MenuItem value="approved">{t('refund.approvedStatus')}</MenuItem>
                <MenuItem value="rejected">{t('refund.rejectedStatus')}</MenuItem>
                <MenuItem value="processing">{t('refund.processingCard')}</MenuItem>
                <MenuItem value="completed">{t('refund.completedTab')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('refund.requestStatus')}</InputLabel>
              <Select
                value={typeFilter}
                label={t('refund.requestStatus')}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="cancel">{t('refund.cancelTypeLabel')}</MenuItem>
                <MenuItem value="exchange">{t('refund.exchangeTypeLabel')}</MenuItem>
                <MenuItem value="refund">{t('refund.refundTypeLabel')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 신청 테이블 */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('refund.requestId')}</TableCell>
                <TableCell>{t('refund.type')}</TableCell>
                <TableCell>{t('refund.orderNumber')}</TableCell>
                <TableCell>{t('refund.customerInfo')}</TableCell>
                <TableCell>{t('refund.productName')}</TableCell>
                <TableCell>{t('refund.requestReason')}</TableCell>
                <TableCell align="right">{t('refund.refundAmount')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('refund.requestedAt')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography>{t('common.loading')}</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography>{t('refund.noHistory')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>{request.id}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(request.type)}
                        label={getTypeText(request.type)}
                        color={getTypeColor(request.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {request.order_items?.orders?.id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {request.order_items?.orders?.profiles?.nick_name || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.order_items?.orders?.profiles?.email || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {request.order_items?.product_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150 }}>
                        {request.reason}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {request.order_items?.total_price && request.type !== 'exchange' ? (
                        <Typography variant="body2" color="error">
                          ₩{request.order_items.total_price.toLocaleString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(request.status)}
                        color={getStatusColor(request.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(request.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetail(request)}
                        >
                          <Visibility />
                        </IconButton>
                        {request.status === 'pending' && (
                          <>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleAction(request, 'approve')}
                            >
                              <CheckCircle />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleAction(request, 'reject')}
                            >
                              <Cancel />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      {/* 상세 정보 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('refund.detailTitle', { id: selectedRequest?.id })}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('refund.requestTypeLabel')}</Typography>
                  <Typography variant="body1">{getTypeText(selectedRequest.type)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('refund.processingStatusLabel')}</Typography>
                  <Chip
                    label={getStatusText(selectedRequest.status)}
                    color={getStatusColor(selectedRequest.status) as any}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('refund.orderNumber')}</Typography>
                  <Typography variant="body1">{selectedRequest.order_items?.orders?.id || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">{t('refund.requestedAt')}</Typography>
                  <Typography variant="body1">{new Date(selectedRequest.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('refund.productInfo')}</Typography>
                  <Typography variant="body1">{selectedRequest.order_items?.product_name || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('refund.customerInfoLabel')}</Typography>
                  <Typography variant="body1">
                    {selectedRequest.order_items?.orders?.profiles?.nick_name || '-'}
                    ({selectedRequest.order_items?.orders?.profiles?.email || '-'})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('refund.requestReasonLabel')}</Typography>
                  <Typography variant="body1">{selectedRequest.reason}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('refund.detailDescription')}</Typography>
                  <Typography variant="body1">{selectedRequest.detail || '-'}</Typography>
                </Grid>
                {selectedRequest.order_items?.total_price && selectedRequest.type !== 'exchange' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">{t('refund.refundAmount')}</Typography>
                    <Typography variant="body1" color="error">
                      ₩{selectedRequest.order_items.total_price.toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                {selectedRequest.admin_note && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">{t('refund.adminNote')}</Typography>
                    <Typography variant="body1">{selectedRequest.admin_note}</Typography>
                  </Grid>
                )}
                {selectedRequest.processed_at && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">{t('refund.processedAt')}</Typography>
                    <Typography variant="body1">{new Date(selectedRequest.processed_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedRequest.image_url && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">{t('refund.attachedImages')}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {Array.isArray(selectedRequest.image_url) ? (
                        selectedRequest.image_url.map((imageUrl, index) => (
                          <Avatar
                            key={index}
                            src={imageUrl}
                            variant="rounded"
                            sx={{ width: 100, height: 100 }}
                          />
                        ))
                      ) : (
                        <Avatar
                          src={selectedRequest.image_url}
                          variant="rounded"
                          sx={{ width: 100, height: 100 }}
                        />
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t('common.close')}</Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button
                color="error"
                onClick={() => handleAction(selectedRequest, 'reject')}
              >
                {t('refund.rejectButton')}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAction(selectedRequest, 'approve')}
              >
                {t('refund.approveButton')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 액션 확인 다이얼로그 */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? t('refund.approveAction') : t('refund.rejectAction')}
        </DialogTitle>
        <DialogContent>
          <Alert severity={actionType === 'approve' ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {actionType === 'approve'
              ? t('refund.approveConfirm')
              : t('refund.rejectConfirm')}
          </Alert>
          <TextField
            fullWidth
            label={t('refund.adminNoteLabel')}
            multiline
            rows={3}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder={t('refund.adminNotePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'primary' : 'error'}
            onClick={handleConfirmAction}
          >
            {actionType === 'approve' ? t('refund.approveButton') : t('refund.rejectButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 결제 취소 오류 다이얼로그 */}
      <Dialog open={paymentErrorDialogOpen} onClose={() => setPaymentErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('refund.paymentCancelFailed')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('refund.paymentCancelFailedDesc')}
          </Alert>
          <Typography variant="body2">
            {t('refund.chooseOption')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('refund.manualCancelOption')}
            </Typography>
            <Typography variant="body2">
              {t('refund.contactTechTeam')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentErrorDialogOpen(false)}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
